import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { ClickableAvatar } from "../../components/ClickableAvatar";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import {
  Search, Send, Check, CheckCheck, Eye, Phone, User, MessageCircle, X,
  Filter, ChevronLeft, Clock, RefreshCw, FileText
} from "lucide-react";
import { format } from "date-fns";

// Status tick icons
const StatusTicks = ({ status }) => {
  switch (status) {
    case "Sent":
      return <Check className="h-3.5 w-3.5 text-zinc-400" />;
    case "Delivered":
      return <CheckCheck className="h-3.5 w-3.5 text-zinc-400" />;
    case "Read":
      return <CheckCheck className="h-3.5 w-3.5 text-blue-500" />;
    case "Failed":
      return <X className="h-3.5 w-3.5 text-red-500" />;
    default:
      return <Clock className="h-3.5 w-3.5 text-zinc-400" />;
  }
};

export default function WhatsAppMessages() {
  const { currentBranch } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showMobileList, setShowMobileList] = useState(true);
  
  // Send message
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Chat scroll ref
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Fetch members on mount
  useEffect(() => {
    fetchMembers();
    fetchTemplates();
  }, [currentBranch, unreadOnly]);

  // Handle memberId from URL params (from Send Test Message)
  useEffect(() => {
    const memberIdFromUrl = searchParams.get("memberId");
    if (memberIdFromUrl && members.length > 0) {
      const member = members.find(m => m.id === memberIdFromUrl);
      if (member) {
        setSelectedMember(member);
        setShowMobileList(false); // Show chat on mobile
        // Clear the URL param
        setSearchParams({});
      }
    }
  }, [members, searchParams, setSearchParams]);

  // Fetch messages when member changes
  useEffect(() => {
    if (selectedMember) {
      fetchMessages(selectedMember.id);
    }
  }, [selectedMember]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (searchQuery) params.append("search", searchQuery);
      if (unreadOnly) params.append("unreadOnly", "true");
      
      const res = await api.get(`/whatsapp/chat/members?${params}`);
      setMembers(res.data.members || []);
    } catch (err) {
      console.error("Failed to fetch members:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (memberId) => {
    setMessagesLoading(true);
    try {
      const res = await api.get(`/whatsapp/chat/history/${memberId}?limit=100`);
      setMessages(res.data.messages || []);
      
      // Mark messages as read
      await api.put(`/whatsapp/chat/mark-read/${memberId}`);
      
      // Update unread count in member list
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, unreadCount: 0 } : m
      ));
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      toast.error("Failed to load chat history");
    } finally {
      setMessagesLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      // Fetch both approved and pending templates that have been synced to Meta
      const res = await api.get("/whatsapp/templates?limit=50");
      // Filter to only show templates with metaTemplateName (synced to Meta)
      const syncedTemplates = (res.data.templates || []).filter(t => 
        t.metaTemplateName && (t.status === 'approved' || t.status === 'pending')
      );
      setTemplates(syncedTemplates);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  const handleSearch = () => {
    fetchMembers();
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setShowMobileList(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedMember) return;
    
    setSending(true);
    try {
      const payload = {
        memberId: selectedMember.id,
        message: newMessage.trim()
      };
      
      if (selectedTemplate) {
        payload.templateId = selectedTemplate.id;
      }
      
      const res = await api.post("/whatsapp/chat/send", payload);
      
      // Add message to list
      setMessages(prev => [...prev, res.data.chatMessage]);
      setNewMessage("");
      setSelectedTemplate(null);
      
      // Refresh member list to update last message time
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setNewMessage(template.bodyText);
    setShowTemplateModal(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="whatsapp-messages-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">
            Messages
          </h1>
          <p className="text-sm text-muted-foreground">
            Chat with your members
          </p>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Left Panel - Member List */}
        <Card className={`w-full md:w-80 lg:w-96 flex-shrink-0 flex flex-col ${!showMobileList && selectedMember ? "hidden md:flex" : "flex"}`}>
          <CardHeader className="pb-3 border-b border-border">
            <div className="space-y-3">
              {/* Search */}
              <div className="flex gap-2">
                <Input
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="h-9"
                  data-testid="member-search"
                />
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Filters */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox
                    checked={unreadOnly}
                    onCheckedChange={setUnreadOnly}
                    data-testid="unread-filter"
                  />
                  <span>Unread only</span>
                </label>
                <Button variant="ghost" size="sm" onClick={fetchMembers} className="h-7 text-xs">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <User className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No conversations yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {members.map((member) => (
                  <div
                    key={member.id}
                    onClick={() => handleSelectMember(member)}
                    className={`p-3 cursor-pointer transition-colors hover:bg-secondary/50 ${
                      selectedMember?.id === member.id ? "bg-secondary" : ""
                    }`}
                    data-testid={`member-item-${member.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <ClickableAvatar
                        src={member.photo}
                        name={member.name}
                        className="h-10 w-10 border border-border"
                        fallbackClassName="bg-primary/10 text-primary text-sm"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm truncate">{member.name}</p>
                          {member.lastMessageAt && (
                            <span className="text-[10px] text-muted-foreground">
                              {format(new Date(member.lastMessageAt), "HH:mm")}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {member.phone}
                        </p>
                      </div>
                      {member.unreadCount > 0 && (
                        <Badge className="bg-green-500 text-white text-[10px] h-5 w-5 p-0 flex items-center justify-center rounded-full">
                          {member.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* Right Panel - Chat Area */}
        <Card className={`flex-1 flex flex-col overflow-hidden ${showMobileList && !selectedMember ? "hidden md:flex" : "flex"}`}>
          {selectedMember ? (
            <>
              {/* Chat Header */}
              <CardHeader className="pb-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 md:hidden"
                    onClick={() => setShowMobileList(true)}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <ClickableAvatar
                    src={selectedMember.photo}
                    name={selectedMember.name}
                    className="h-10 w-10 border border-border"
                    fallbackClassName="bg-primary/10 text-primary"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{selectedMember.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {selectedMember.phone}
                    </p>
                  </div>
                </div>
              </CardHeader>

              {/* Messages Area */}
              <ScrollArea 
                ref={chatContainerRef}
                className="flex-1 p-4"
                style={{ background: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.02) 10px, rgba(0,0,0,0.02) 20px)" }}
              >
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No messages yet</p>
                    <p className="text-xs">Start a conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => {
                      const isOutgoing = msg.messageType === "outgoing";
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOutgoing ? "justify-end" : "justify-start"}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-lg p-3 ${
                              isOutgoing
                                ? "bg-[#DCF8C6] dark:bg-[#025C4C] rounded-tr-none"
                                : "bg-white dark:bg-zinc-800 rounded-tl-none shadow-sm"
                            }`}
                          >
                            {/* Message Content */}
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {msg.messageContent || msg.template?.bodyText || "Message"}
                            </p>
                            
                            {/* Template indicator */}
                            {msg.templateId && msg.template && (
                              <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {msg.template.name}
                              </p>
                            )}
                            
                            {/* Timestamp & Status */}
                            <div className="flex items-center justify-end gap-1 mt-1">
                              <span className="text-[10px] text-muted-foreground">
                                {format(new Date(msg.sentAt || msg.createdAt), "HH:mm")}
                              </span>
                              {isOutgoing && <StatusTicks status={msg.status} />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Message Input */}
              <div className="p-4 border-t border-border bg-card">
                {/* 24-hour window notice */}
                <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <p className="text-[10px] text-amber-600 dark:text-amber-400">
                    <strong>Note:</strong> Text messages only work if the recipient messaged your business number (+1 555 171 3012) within the last 24 hours. 
                    Use <button onClick={() => setShowTemplateModal(true)} className="underline font-medium">Templates</button> to message anytime.
                  </p>
                </div>
                
                {selectedTemplate && (
                  <div className="mb-2 p-2 bg-secondary/50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-xs">Using template: {selectedTemplate.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        setSelectedTemplate(null);
                        setNewMessage("");
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                
                <div className="flex items-end gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowTemplateModal(true)}
                    title="Use Template (Works anytime)"
                    data-testid="template-btn"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message... (requires 24hr window)"
                    className="min-h-[40px] max-h-[120px] resize-none"
                    rows={1}
                    data-testid="message-input"
                  />
                  
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    data-testid="send-btn"
                  >
                    {sending ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                <h3 className="font-heading font-semibold text-lg mb-1">No Chat Selected</h3>
                <p className="text-sm text-muted-foreground">
                  Select a member from the list to start chatting
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Template Selection Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-md max-h-[70vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide">
              Select Template
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-2 py-4">
              {templates.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No approved templates available
                </p>
              ) : (
                templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className="p-3 rounded-lg border border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-sm">{template.name}</p>
                      <Badge variant="outline" className="text-[10px]">{template.category}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{template.bodyText}</p>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
